import {
  ClosedReferrals,
  OpenReferrals,
  AllClaimants,
  ReviewedReferrals,
  AssignedToWriters,
} from 'Features/CaseManagement';
import { ReferralsRequests } from 'Features/ReferralsRequests';
import { Appointments } from 'Features/Appointments';
import { DraftReferrals as DraftReferralsLegacy } from 'Features/DraftReferrals';
import { DraftReferrals } from 'v3/Components/Pages/DraftReferrals/DraftReferrals';
import { Permission } from 'Models/Templates/Permission/Permission';
import { DropdownSubRouteConfig, SubRoutes } from 'Navigation/NavigationTypes';
import React from 'react';
import { FeatureFlags, isFeatureEnabled } from 'Utilities';
import { UnderReviewQueuePage } from 'v3/Components/Pages/UnderReviewQueue/UnderReviewQueue';
import { AssigningQueuePage } from 'v3/Components/Pages/AssigningQueue/AssigningQueue';
import { AllCases } from 'v3/Components/Pages/AllCases/AllCases';
import { IntakeQueue } from 'v3/Components/Pages/IntakeQueue/IntakeQueue';
import { ClosedCases } from 'v3/Components/Pages/ClosedCases/ClosedCases';
import { OpenCases } from 'v3/Components/Pages/OpenCases/OpenCases';
import MenuBook from '@mui/icons-material/MenuBook';
import Book from '@mui/icons-material/Book';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import GradingIcon from '@mui/icons-material/Grading';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';

export enum CaseManagementSubRoute {
  OpenReferrals = '/OpenReferrals',
  OpenCases = '/OpenCases',
  ClosedReferrals = '/ClosedReferrals',
  ClosedCases = '/ClosedCases',
  IntakeQueue = '/IntakeQueue',
  DraftReferrals = '/DraftReferrals',
  AssigningQueue = '/AssigningQueue',
  AllClaimants = '/AllClaimants',
  AllCases = '/AllCases',
  QAQueue = '/QAQueue',
  UnderReviewQueue = '/UnderReviewQueue',
}

// FeatureFlags enum is unavailable at module init due to a circular dependency through
// FeatureFlagProvider → Components/Navigation → Navigation → SubRoutesMap → here.
// checkFlag reads window/process.env directly to resolve routes before that cycle is broken.
function checkFlag(flag: string): boolean {
  const windowVal = (window as Record<string, any>)[flag];
  if (windowVal != null && !String(windowVal).startsWith('#{')) {
    return String(windowVal).toLowerCase() === 'true';
  }
  return process.env[`REACT_APP_${flag}`] === 'true';
}

const v3AllCasesEnabled = checkFlag('V3_ALL_CASES');
const v3DraftReferralsEnabled = checkFlag('V3_DRAFT_REFERRALS');
const v3ClosedCasesEnabled = checkFlag('V3_CLOSED_CASES');
const v3OpenCasesEnabled = checkFlag('V3_OPEN_CASES');
const v3IntakeQueueEnabled = checkFlag('V3_INTAKE_QUEUE');
const v3AssigningQueueEnabled = checkFlag('V3_ASSIGNING_QUEUE');

const UnderReviewQueueRoute = React.forwardRef<any, any>((props, _ref) =>
  isFeatureEnabled(FeatureFlags.V3UnderReviewQueue)
    ? <UnderReviewQueuePage {...props} />
    : <AssignedToWriters {...props} />
);

// When a V3 page is enabled, it's registered under both the new "Cases" URL (used by
// internal/reviewer users) and the legacy "Referrals" URL (used by client-type users, to
// match the old client dashboard). NavigationHelpers picks the right one per user type.
const openSubRoutes: SubRoutes = v3OpenCasesEnabled
  ? {
      [CaseManagementSubRoute.OpenCases]: new DropdownSubRouteConfig(
        CaseManagementSubRoute.OpenCases,
        'Open Cases',
        OpenCases,
        [Permission.ViewOpenCases],
        true,
        {},
        <MenuBook />
      ),
      [CaseManagementSubRoute.OpenReferrals]: new DropdownSubRouteConfig(
        CaseManagementSubRoute.OpenReferrals,
        'Open Referrals',
        OpenCases,
        [Permission.ViewOpenCases],
        true,
        {},
        <MenuBook />
      ),
    }
  : {
      [CaseManagementSubRoute.OpenReferrals]: new DropdownSubRouteConfig(
        CaseManagementSubRoute.OpenReferrals,
        'Open Referrals',
        OpenReferrals,
        [Permission.ViewOpenCases],
        true,
        {},
        <MenuBook />
      ),
    };

const closedSubRoutes: SubRoutes = v3ClosedCasesEnabled
  ? {
      [CaseManagementSubRoute.ClosedCases]: new DropdownSubRouteConfig(
        CaseManagementSubRoute.ClosedCases,
        'Closed Cases',
        ClosedCases,
        [Permission.ViewClosedCases],
        false,
        {},
        <Book />
      ),
      [CaseManagementSubRoute.ClosedReferrals]: new DropdownSubRouteConfig(
        CaseManagementSubRoute.ClosedReferrals,
        'Closed Referrals',
        ClosedCases,
        [Permission.ViewClosedCases],
        false,
        {},
        <Book />
      ),
    }
  : {
      [CaseManagementSubRoute.ClosedReferrals]: new DropdownSubRouteConfig(
        CaseManagementSubRoute.ClosedReferrals,
        'Closed Referrals',
        ClosedReferrals,
        [Permission.ViewClosedCases],
        false,
        {},
        <Book />
      ),
    };

const allSubRoutes: SubRoutes = v3AllCasesEnabled
  ? {
      [CaseManagementSubRoute.AllCases]: new DropdownSubRouteConfig(
        CaseManagementSubRoute.AllCases,
        'All Cases',
        AllCases,
        [Permission.ViewAllClaimants],
        false,
        {},
        <Book />
      ),
      [CaseManagementSubRoute.AllClaimants]: new DropdownSubRouteConfig(
        CaseManagementSubRoute.AllClaimants,
        'All Referrals',
        AllCases,
        [Permission.ViewAllClaimants],
        false,
        {},
        <Book />
      ),
    }
  : {
      [CaseManagementSubRoute.AllClaimants]: new DropdownSubRouteConfig(
        CaseManagementSubRoute.AllClaimants,
        'All Referrals',
        AllClaimants,
        [Permission.ViewAllClaimants],
        false,
        {},
        <Book />
      ),
    };

export const CaseManagementSubRoutes: SubRoutes = {
  [CaseManagementSubRoute.DraftReferrals]: new DropdownSubRouteConfig(
    CaseManagementSubRoute.DraftReferrals,
    'Referral Drafts',
    v3DraftReferralsEnabled ? DraftReferrals : DraftReferralsLegacy,
    [Permission.ViewDraftReferrals],
    false,
    {},
    <NoteAddIcon />
  ),
  [CaseManagementSubRoute.IntakeQueue]: new DropdownSubRouteConfig(
    CaseManagementSubRoute.IntakeQueue,
    'Intake Queue',
    v3IntakeQueueEnabled ? IntakeQueue : ReferralsRequests,
    [Permission.ViewNewReferrals],
    false,
    {},
    <NoteAddIcon />
  ),
  [CaseManagementSubRoute.AssigningQueue]: new DropdownSubRouteConfig(
    CaseManagementSubRoute.AssigningQueue,
    'Assigning Queue',
    v3AssigningQueueEnabled ? AssigningQueuePage : Appointments,
    [Permission.ViewPendingAppointments],
    false,
    {},
    <BookOnlineIcon />
  ),
  ...openSubRoutes,
  ...closedSubRoutes,
  ...allSubRoutes,
  [CaseManagementSubRoute.QAQueue]: new DropdownSubRouteConfig(
    CaseManagementSubRoute.QAQueue,
    'QA Queue',
    ReviewedReferrals,
    [Permission.ViewReviewedReferrals],
    false,
    {},
    <GradingIcon />
  ),
  [CaseManagementSubRoute.UnderReviewQueue]: new DropdownSubRouteConfig(
    CaseManagementSubRoute.UnderReviewQueue,
    'Under Review Queue',
    UnderReviewQueueRoute,
    [Permission.ViewAssignedToWriters],
    false,
    {},
    <DriveFileRenameOutlineIcon />
  ),
};
